"use client"

import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { analyticsAttrs, type AnalyticsEvent } from "@/lib/analytics/attrs"
// import { useMediaQuery } from "@/lib/useMediaQuery"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-full text-base font-bold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40",
  {
    variants: {
      variant: {
        default:
          "bg-purple text-white hover:bg-purple-500 focus-visible:ring-purple",
        purple:
          "bg-purple text-white hover:bg-purple-500 focus-visible:ring-purple",
        red: "bg-red text-white hover:bg-red-500 focus-visible:ring-red",
        "white-purple":
          "bg-white text-purple hover:bg-white hover:text-purple-500 focus-visible:ring-focus",
        "white-red":
          "bg-white text-red hover:bg-white hover:text-red-500 focus-visible:ring-focus",
        "outline-purple":
          "border border-purple bg-transparent text-purple hover:bg-purple hover:text-white focus-visible:ring-focus",
        "outline-red":
          "border border-red bg-transparent text-red hover:bg-red hover:text-white focus-visible:ring-focus",
        "outline-white-purple":
          "border border-input bg-transparent text-white hover:bg-white hover:text-purple-500 focus-visible:ring-focus",
        "outline-white-red":
          "border border-input bg-transparent text-white hover:bg-white hover:text-red-500 focus-visible:ring-focus",
        "light-purple":
          "bg-purple-300 text-purple hover:bg-purple hover:text-white focus-visible:ring-focus",
        "outline-black":
          "border border-black bg-transparent text-black hover:bg-black hover:text-white focus-visible:ring-focus",
        outline:
          "border border-purple-300 bg-transparent hover:bg-purple-100 focus-visible:ring-focus",
        ghost: "hover:bg-purple-100 focus-visible:ring-focus"
      },
      size: {
        default: "h-14 px-8 py-4",
        small: "h-10 px-4 py-2",
        icon: "h-14 w-14",
        iconSmall: "h-10 w-10",
        responsive: "h-10 px-4 py-2 lg:h-14 lg:px-8 lg:py-4"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  analytics?: AnalyticsEvent
  analyticsEventName?: string
  analyticsProps?: Record<string, unknown>
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      analytics,
      analyticsEventName,
      analyticsProps,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    // const useSmallButtons = useMediaQuery("(max-width: 1023px)")

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...analyticsAttrs(analytics, {
          eventName: analyticsEventName,
          props: analyticsProps
        })}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
